'use client';

import { useEffect, useMemo, useState } from 'react';

import { AddUserChat } from './add-user-chat.modal';
import { ChatItem } from './chat-item';
import { CollapsedChatItem } from './collapsed-chat-item';

import { ChatData } from '@/slw/entities/chat';
import { SearchInput } from '@/slw/features/chat/search-input.ui';
import { Skeleton } from '@/slw/shared/ui/skeleton';
import { useDebounce } from '@/slw/shared/hooks/use-debounce';
import { useChatStore } from '@/slw/shared/lib/firebase/chat-store';

interface SidebarProps {
  isCollapsed: boolean;
  chats: ChatData[];
  onClick?: () => void;
  isMobile?: boolean;
  onChatSelect?: (UserC: ChatData) => void;
  selectedChatId: string | null;
  loading: boolean;
}

export function SidebarSkeleton({ isCollapsed }: { isCollapsed: boolean }) {
  return (
    <div
      data-collapsed={isCollapsed}
      className='relative group flex flex-col h-full bg-muted/10 dark:bg-muted/20 gap-4 p-2 data-[collapsed=true]:p-2'
    >
      {!isCollapsed && (
        <div className='flex justify-between p-2 items-center'>
          <div className='flex gap-2 items-center'>
            <Skeleton className='h-8 w-16' />
            <Skeleton className='h-6 w-8' />
          </div>
          <Skeleton className='h-9 w-9' />
        </div>
      )}

      <nav
        className='grid overflow-y-auto overflow-x-hidden gap-1 px-2 group-[[data-collapsed=true]]:justify-center 
        group-[[data-collapsed=true]]:px-2'
      >
        {!isCollapsed && <Skeleton className='h-10 w-full' />}

        {Array.from({ length: 5 }).map((_, index) =>
          isCollapsed ? (
            <Skeleton key={index} className='h-11 w-11 md:h-16 md:w-16 rounded-full' />
          ) : (
            <Skeleton key={index} className='h-16 w-full' />
          ),
        )}
      </nav>
    </div>
  );
}

export function Sidebar({ chats, isCollapsed, onChatSelect, selectedChatId, loading }: SidebarProps) {
  const [isAddChatModalOpen, setIsAddChatModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(loading);
  const { setSearchChat, searchChat } = useChatStore();
  const searchValue = useDebounce(searchChat, 1000);

  useEffect(() => {
    setIsLoading(loading);
  }, [loading]);

  const filteredChats = useMemo(() => {
    if (!searchValue.trim()) return chats;

    return chats.filter((chat) => chat?.user?.username.toLowerCase().includes(searchValue.toLowerCase()));
  }, [chats, searchValue]);

  if (isLoading) {
    return <SidebarSkeleton isCollapsed={isCollapsed} />;
  }

  return (
    <div
      data-collapsed={isCollapsed}
      className='relative group flex flex-col h-full bg-muted/10 dark:bg-muted/20 gap-4
       sm:p-0 sm:data-[collapsed=true]:py-2 sm:data-[collapsed=true]:px-0 p-2'
    >
      {!isCollapsed && <SearchInput value={searchChat} onChange={setSearchChat} />}

      <nav
        className='grid overflow-y-auto overflow-x-hidden gap-1 group-[[data-collapsed=true]]:justify-center 
      md:group-[[data-collapsed=true]]:px-2 group-[[data-collapsed=true]]:px-1'
      >
        {filteredChats.map((chat, filteredIndex) => {
          const isSelected = selectedChatId === chat.chatId;
          const handleClick = () => onChatSelect?.(chat);

          return isCollapsed ? (
            <CollapsedChatItem key={filteredIndex} chat={chat} isSelected={isSelected} onClick={handleClick} />
          ) : (
            <ChatItem key={filteredIndex} chat={chat} isSelected={isSelected} onClick={handleClick} />
          );
        })}
      </nav>
      {isAddChatModalOpen && <AddUserChat open={isAddChatModalOpen} onClose={() => setIsAddChatModalOpen(false)} />}
    </div>
  );
}
